import React, { useState, useEffect } from "react";
import { FaStar } from "react-icons/fa";
import StatusModal from "../../../components/modals/statusModal/StatusModal";
import { requestBadge } from "../../../utils/badgeApi";
import { useAuth } from "../../../context/AuthContext";
import "./EarnBadgeModal.css";

// Per-badge-type form config. `anyOf` means at least one of those fields is
// required (not all) — used for Training Centre Registration, which accepts
// either a registration number or a CAC number.
const BADGE_FORM_CONFIG = {
  qualification_verified: {
    title: "Add Qualification Verification",
    description:
      "Verify your educational qualifications to build trust with students.",
    fields: [
      { name: "degree", label: "Degree", placeholder: "e.g., B.Sc Computer Science" },
      { name: "schoolName", label: "School Name", placeholder: "e.g., University of Lagos" },
      { name: "fieldOfStudy", label: "Field of Study", placeholder: "e.g., Computer Science" },
    ],
  },
  professional_certificate_verified: {
    title: "Add Professional License & Certification",
    description:
      "Upgrade your profile by providing your professional license and certification information.",
    fields: [
      {
        name: "licenseName",
        label: "License / Certificate Name",
        placeholder: "e.g., PMP Certification, Cisco CCNA",
      },
      {
        name: "issuingOrganization",
        label: "Issuing Organization",
        placeholder: "e.g., Project Management Institute, Cisco",
      },
    ],
  },
  training_centre_registration_verified: {
    title: "Add Training Centre Registration",
    description: "Verify your training centre's official registration.",
    fields: [
      {
        name: "registrationNumber",
        label: "Registration Number",
        placeholder: "e.g., RC1234567",
      },
      { name: "cacNumber", label: "CAC Number", placeholder: "e.g., BN1234567" },
    ],
    anyOf: ["registrationNumber", "cacNumber"],
  },
};

const EarnBadgeModal = ({ open, onClose, badgeType }) => {
  const { updateUser } = useAuth();
  const config = BADGE_FORM_CONFIG[badgeType];

  const [values, setValues] = useState({});
  const [supportingFile, setSupportingFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [statusModal, setStatusModal] = useState({
    open: false,
    type: "success",
    message: "",
    subtext: "",
  });

  // Reset the form whenever a different badge type is opened
  useEffect(() => {
    if (config) {
      setValues(config.fields.reduce((acc, f) => ({ ...acc, [f.name]: "" }), {}));
      setSupportingFile(null);
    }
  }, [badgeType]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!open || !config) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setValues((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) setSupportingFile(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) setSupportingFile(file);
  };

  const isValid = config.anyOf
    ? config.anyOf.some((f) => values[f]?.trim())
    : config.fields.every((f) => values[f.name]?.trim());

  const canSubmit = isValid && supportingFile;

  const handleSubmit = async () => {
    if (!canSubmit) return;

    setIsSubmitting(true);
    try {
      const res = await requestBadge(badgeType, values, supportingFile);

      updateUser({ badges: res.badges });

      onClose();

      setStatusModal({
        open: true,
        type: "success",
        message: "Request Submitted",
        subtext:
          "Your credentials are under review. We'll notify you once verified.",
      });
    } catch (err) {
      setStatusModal({
        open: true,
        type: "error",
        message: "Submission Failed",
        subtext:
          err.response?.data?.message ||
          "Something went wrong. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTextField = (field) => (
    <div className="badge-form-group" key={field.name}>
      <label>{field.label} *</label>
      <input
        type="text"
        name={field.name}
        value={values[field.name] || ""}
        onChange={handleInputChange}
        placeholder={field.placeholder}
        className="badge-input"
      />
    </div>
  );

  return (
    <>
      <div className="badge-modal-backdrop" onClick={onClose}>
        <div className="badge-modal" onClick={(e) => e.stopPropagation()}>
          {/* Header */}
          <div className="badge-modal-header">
            <div className="badge-modal-title-group">
              <div className="badge-chip">
                <FaStar style={{ fontSize: "10px" }} />
                Badge program
              </div>
              <h2>{config.title}</h2>
              <p>{config.description}</p>
            </div>
            <button className="badge-close-btn" onClick={onClose}>
              ✕
            </button>
          </div>

          <hr className="badge-divider" />

          {config.fields.map(renderTextField)}

          <hr className="badge-divider" />

          {/* File upload */}
          <div className="badge-form-group">
            <label>Upload supporting document *</label>
            <div
              className={`file-upload-zone ${isDragging ? "dragging" : ""}`}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragging(true);
              }}
              onDragLeave={() => setIsDragging(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById("badgeFileInput").click()}
            >
              <input
                id="badgeFileInput"
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                hidden
                onChange={handleFileChange}
              />
              {supportingFile ? (
                <p className="file-selected-name">📄 {supportingFile.name}</p>
              ) : (
                <>
                  <div className="file-upload-icon">📎</div>
                  <p className="file-upload-text">
                    Drag &amp; drop or <span>browse file</span>
                  </p>
                  <p className="file-upload-hint">PDF, JPG, PNG — max 5MB</p>
                </>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="badge-modal-actions">
            <button className="badge-cancel-btn" onClick={onClose}>
              Cancel
            </button>
            <button
              className="badge-submit-btn"
              onClick={handleSubmit}
              disabled={!canSubmit || isSubmitting}
            >
              {isSubmitting ? <span className="spinner" /> : "Submit for review"}
            </button>
          </div>
        </div>
      </div>

      <StatusModal
        open={statusModal.open}
        type={statusModal.type}
        message={statusModal.message}
        subtext={statusModal.subtext}
        onClose={() => setStatusModal({ ...statusModal, open: false })}
      />
    </>
  );
};

export default EarnBadgeModal;
